import { Style, InputType, DynamicUpdateAction, DynamicUpdateCondition } from 'common/questionnaires/types';
import helpText from 'common/questionnaires/helpText';

const crseQuestions = [
  {
    id: 'CRSE.NAME.1',
    name: 'legalName',
    info: helpText.legalName,
    questionText: 'preQualifyingQuestionnaire.CRSE.NAME.1.questionText',
    style: Style.Card,
    group: 'Legal Name',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Legal Name of Entity/Branch', required: true }
      ]
    },
    errorMessage: 'Please provide a legal name.'
  },
  {
    id: 'CRSE.COUNTRYOFINCORP',
    name: 'countryOfIncorporation',
    style: Style.Card,
    info: helpText.countryOfIncorporationOrOrganization,
    questionText: 'preQualifyingQuestionnaire.CRSE.COUNTRYOFINCORP',
    group: 'Country of Incorporation or Organisation',
    inputs: {
      type: InputType.CountryDropdown,
      invalidSelection: 'Please Select Country',
      defaultSelection: 'Please Select Country'
    },
    errorMessage: 'Please provide a country.'
  },
  {
    id: 'CRSE.ADDRESS.1',
    name: 'residenceAddressLine1',
    info: helpText.residenceAddress,
    questionText: 'preQualifyingQuestionnaire.W8BEN.ADDRESS.1.questionText',
    style: Style.Card,
    group: 'Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Street Address', required: true }
      ]
    },
    errorMessage: 'Please provide a street address.'
  },
  {
    id: 'CRSE.ADDRESS.2',
    name: 'residenceCityStateProvince',
    style: Style.Embedded,
    group: 'Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'City/State/Province/Territory', required: true }
      ]
    },
    errorMessage: 'Please provide a city, state, province, or territory.'
  },
  {
    id: 'CRSE.ADDRESS.3',
    name: 'residenceCountry',
    style: Style.Embedded,
    group: 'Residence Address',
    inputs: {
      type: InputType.CountryDropdown,
      invalidSelection: 'Please Select Country',
      defaultSelection: 'Please Select Country'
    },
    errorMessage: 'Please provide a country.'
  },
  {
    id: 'CRSE.ADDRESS.4',
    name: 'residencePostalcode',
    style: Style.Embedded,
    group: 'Residence Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Postal Code' }
      ]
    }
  },
  {
    id: 'CRSE.MAILADDRESS.1',
    name: 'mailAddressLine1',
    info: helpText.mailingAddress,
    questionText: 'preQualifyingQuestionnaire.W8BEN.MAILADDRESS.1.questionText',
    style: Style.Card,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Street Address', required: false }
      ]
    },
    partialFieldConstraint: ['CRSE.MAILADDRESS.2', 'CRSE.MAILADDRESS.3'],
    errorMessage: 'Please provide a street address.'
  },
  {
    id: 'CRSE.MAILADDRESS.2',
    name: 'mailCityStateProvince',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'City/State/Province/Territory', required: false }
      ]
    },
    partialFieldConstraint: ['CRSE.MAILADDRESS.1', 'CRSE.MAILADDRESS.3'],
    errorMessage: 'Please provide a city, state, province, or territory.'
  },
  {
    id: 'CRSE.MAILADDRESS.3',
    name: 'mailCountry',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.CountryDropdown,
      defaultValue: { value: '0', label: 'Please Select Country' },
    },
    partialFieldConstraint: ['CRSE.MAILADDRESS.1', 'CRSE.MAILADDRESS.2'],
    errorMessage: 'Please provide a country.'
  },
  {
    id: 'CRSE.MAILADDRESS.4',
    name: 'mailPostalCode',
    style: Style.Embedded,
    group: 'Mailing Address',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Postal Code', required: false }
      ]
    },
  },
  {
    id: 'CRSE.ENTITYTYPE.1',
    name: 'entityType1',
    style: Style.Card,
    group: 'Entity Type',
    info: helpText.crse.entityType,
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.1.questionText',
    inputs: {
      type: InputType.Dropdown,
      invalidSelection: 'Please Select Entity Type',
      items: [
        { value: 0, label: 'Please Select Entity Type' },
        { value: 1, label: 'Financial Institution' },
        { value: 2, label: 'Active NFE' },
        { value: 3, label: 'Passive NFE' },
      ]
    },
    errorMessage: 'Please provide an entity type.'
  },
  {
    id: 'CRSE.ENTITYTYPE.2',
    name: 'financialInsitutionType',
    style: Style.Embedded,
    group: 'Entity Type',
    info: helpText.crse.financialInstitutionType,
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.2.questionText',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '1',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.Dropdown,
      invalidSelection: 'Please Select Financial Institution Type',
      items: [
        { value: 0, label: 'Please Select Financial Institution Type' },
        { value: 1, label: 'Investment Entity' },
        { value: 2, label: 'Depository/Custodial' },
        { value: 3, label: 'Specified Insurance Company' },
      ]
    },
    errorMessage: 'Please provide a financial institution type.'
  },
  {
    id: 'CRSE.ENTITYTYPE.3',
    name: 'entityNonParticipatingCountry',
    info: helpText.crse.entityNonParticipatingCountry,
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.3.questionText',
    style: Style.Embedded,
    group: 'Entity Type',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.2',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '1',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.YesNo,
      required: true,
      buttonTitles: [
        'Yes', 'No'
      ]
    },
    errorMessage: 'Please indicate the nature of the investment entity.'
  },
  {
    id: 'CRSE.ENTITYTYPE.4',
    name: 'activeNffeType',
    style: Style.Embedded,
    group: 'Entity Type',
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.4.questionText',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '2',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.Dropdown,
      defaultSelection: 'Please Select Active NFE Type',
      invalidSelection: 'Please Select Active NFE Type',
      items: [
        { value: 0, label: 'Please Select Active NFE Type' },
        { value: 1, label: 'Regularly Traded Corporation' },
        { value: 2, label: 'Related Entity of Regularly Traded Corporation' },
        { value: 3, label: 'Government Entity/Central Bank' },
        { value: 4, label: 'International Organisation' },
        { value: 5, label: 'Other' },
      ]
    },
    errorMessage: 'Please provide active NFE type.'
  },
  {
    id: 'CRSE.ENTITYTYPE.5',
    name: 'securitiesMarket',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.5.questionText',
    group: 'Entity Type',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.4',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '1',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Securities market', required: true },
      ]
    },
    errorMessage: 'Please specify the securities exchange the corporation trades on.'
  },
  {
    id: 'CRSE.ENTITYTYPE.6',
    name: 'relatedEntity',
    info: helpText.crse.relatedEntity,
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.ENTITYTYPE.6.questionText',
    group: 'Entity Type',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.4',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '2',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Related entity', required: true },
      ]
    },
    errorMessage: 'Please provide the name of the regularly traded corporation.'
  },
  {
    id: 'CRSE.GIIN.1',
    name: 'giin',
    style: Style.Card,
    info: helpText.giin,
    questionText: 'preQualifyingQuestionnaire.CRSE.GIIN.1.questionText',
    group: 'GIIN',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '1',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.GiinField,
      fields: [
        { placeholder: 'Enter GIIN', required: true }
      ]
    },
    errorMessage: 'Please provide a GIIN'
  },
  {
    id: 'CRSE.CP.1',
    name: 'controllingPersons1',
    style: Style.Card,
    info: helpText.controllingPersons,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.1.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 1', required: false },
      ]
    }
  },
  {
    id: 'CRSE.CP.2',
    name: 'controllingPersons2',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.2.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 2', required: false },
      ]
    }
  },
  {
    id: 'CRSE.CP.3',
    name: 'controllingPersons3',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.3.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 3', required: false },
      ]
    }
  },
  {
    id: 'CRSE.CP.4',
    name: 'controllingPersons4',
    style: Style.Card,
    info: helpText.controllingPersons,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.1.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '3',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 1', required: false },
      ]
    }
  },
  {
    id: 'CRSE.CP.5',
    name: 'controllingPersons5',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.2.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '3',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 2', required: false },
      ]
    }
  },
  {
    id: 'CRSE.CP.6',
    name: 'controllingPersons6',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.CP.3.questionText',
    group: 'Controlling Persons',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.ENTITYTYPE.1',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: '3',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Controlling persons 3', required: false },
      ]
    }
  },
  {
    id: 'CRSE.TIN.1',
    name: 'countryOfTaxResidence1',
    style: Style.Card,
    group: 'Tax Identification Number (TIN)',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.1.questionText',
    inputs:
    {
      type: InputType.CountryDropdown,
      invalidSelection: 'Select a Country of Tax Residence',
      defaultSelection: 'Select a Country of Tax Residence',
      defaultValue: { value: 'Select a Country of Tax Residence', label: 'Select a Country of Tax Residence' },
    },
    errorMessage: 'Please select a country.'
  },
  {
    id: 'CRSE.TIN.2',
    name: 'tin1',
    info: helpText.crs.tin,
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.2.questionText',
    group: 'Tax Identification Number (TIN)',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'TIN', required: false, validation: 'ftin' },
      ]
    },
    errorMessage: 'Please provide a TIN'
  },
  {
    id: 'CRSE.TIN.3',
    name: 'tinreason1',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.6.questionText',
    inputs: {
      type: InputType.Dropdown,
      defaultSelection: 'Please Select Reason',
      defaultValue: { value: 'Please Select Reason', label: 'Please Select Reason' },
      items: [
        { value: 'Please Select Reason', label: 'Please Select Reason' },
        { value: 'A', label: 'The country where the account holder is liable to pay tax does not issue TINs to its residents' },
        { value: 'B', label: 'The Account Holder is otherwise unable to obtain a TIN or equivalent number' },
        { value: 'C', label: 'No TIN is required.' },

      ]
    },
  },
  {
    id: 'CRSE.TIN.4',
    name: 'reason1',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.7.questionText',
    group: 'Tax Identification Number (TIN)',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 'B',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Residence 1 Reason', required: true },
      ]
    },
    errorMessage: 'Please provide a reason why you are unable to obtain a TIN where you have indicated you are otherwise unable to obtain a TIN or equivalent number.'
  },
  {
    id: 'CRSE.TIN.8',
    name: 'showOtherTinFields1',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.3.questionText',
    group: 'Tax Identification Number (TIN)',
    answer: 1,
    isAnswered: true,
    inputs: {
      type: InputType.YesNo,
      buttonTitles: [
        'Yes', 'No'
      ]
    }
  },
  {
    id: 'CRSE.TIN2.1',
    name: 'countryOfTaxResidence2',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.4.questionText',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.8',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs:
    {
      type: InputType.CountryDropdown,
      invalidSelection: 'Select a Country of Tax Residence',
      defaultSelection: 'Select a Country of Tax Residence',
      defaultValue: { value: 'Select a Country of Tax Residence', label: 'Select a Country of Tax Residence' },
    },
    errorMessage: 'Please select a country.'
  },
  {
    id: 'CRSE.TIN2.2',
    name: 'tin2',
    info: helpText.foreignTinEntities,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.2.questionText',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'TIN', required: false, validation: 'ftin' },
      ]
    },
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.8',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN2.3',
    name: 'tinReason2',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.6.questionText',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    inputs: {
      type: InputType.Dropdown,
      defaultSelection: 'Please Select Reason',
      defaultValue: { value: 'Please Select Reason', label: 'Please Select Reason' },

      items: [
        { value: 'Please Select Reason', label: 'Please Select Reason' },
        { value: 'A', label: 'The country where the account holder is liable to pay tax does not issue TINs to its residents' },
        { value: 'B', label: 'The Account Holder is otherwise unable to obtain a TIN or equivalent number' },
        { value: 'C', label: 'No TIN is required.' },
      ]
    },
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.8',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN.5',
    name: 'reason2',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.7.questionText',
    group: 'Tax Identification Number (TIN)',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN2.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 'B',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Residence 2 Reason', required: true },
      ]
    },
    errorMessage: 'Please provide a reason why you are unable to obtain a TIN where you have indicated you are otherwise unable to obtain a TIN or equivalent number.'
  },
  {
    id: 'CRSE.TIN.9',
    name: 'showOtherTinFields2',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.3.questionText',
    group: 'Tax Identification Number (TIN)',
    answer: 1,
    isAnswered: true,
    inputs: {
      type: InputType.YesNo,
      buttonTitles: [
        'Yes', 'No'
      ]
    },
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.8',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN3.1',
    name: 'countryOfTaxResidence3',
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.5.questionText',
    inputs:
    {
      type: InputType.CountryDropdown,
      invalidSelection: 'Select a Country of Tax Residence',
      defaultSelection: 'Select a Country of Tax Residence',
      defaultValue: { value: 'Select a Country of Tax Residence', label: 'Select a Country of Tax Residence' },
    },
    errorMessage: 'Please select a country.',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.9',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN3.2',
    name: 'tin3',
    info: helpText.foreignTinEntities,
    style: Style.Embedded,
    group: 'Tax Identification Number (TIN)',
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.2.questionText',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'TIN', required: false, validation: 'ftin' },
      ]
    },
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.9',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN3.3',
    name: 'tinReason3',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.6.questionText',
    group: 'Tax Identification Number (TIN)',
    inputs: {
      type: InputType.Dropdown,
      defaultSelection: 'Please Select Reason',
      defaultValue: { value: '0', label: 'Please Select Reason' },
      items: [
        { value: 'Please Select Reason', label: 'Please Select Reason' },
        { value: 'A', label: 'The country where the account holder is liable to pay tax does not issue TINs to its residents' },
        { value: 'B', label: 'The Account Holder is otherwise unable to obtain a TIN or equivalent number' },
        { value: 'C', label: 'No TIN is required.' },

      ]
    },
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN.9',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 0,
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
  },
  {
    id: 'CRSE.TIN.6',
    name: 'reason3',
    style: Style.Embedded,
    questionText: 'preQualifyingQuestionnaire.CRSE.TIN.7.questionText',
    group: 'Tax Identification Number (TIN)',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.TIN3.3',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 'B',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Residence 3 Reason', required: true },
      ]
    },
    errorMessage: 'Please provide a reason why you are unable to obtain a TIN where you have indicated you are otherwise unable to obtain a TIN or equivalent number.'
  },
  {
    id: 'CRSE.Signer.1',
    name: 'signerName',
    info: helpText.signerName,
    questionText: 'preQualifyingQuestionnaire.CRSE.Signer.1.questionText',
    style: Style.Card,
    group: 'Signer Details',
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Signer Name', required: true }
      ]
    },
    errorMessage: 'Required: Signer name is required.'
  },
  {
    id: 'CRSE.Signer.2',
    name: 'capacityField',
    info: helpText.capacity,
    questionText: 'preQualifyingQuestionnaire.CRSE.Signer.2.questionText',
    style: Style.Embedded,
    group: 'Signer Details',
    inputs: {
      type: InputType.Dropdown,
      defaultValue: { value: 'Please Select', label: 'Please Select' },
      invalidSelection: 'Please Select',
      items: [
        { value: 'Please Select', label: 'Please Select' },
        { value: 'Conservator', label: 'Conservator' },
        { value: 'Authorized Signer', label: 'Authorized Signer' },
        { value: 'Secretary', label: 'Secretary' },
        { value: 'Controlling Person', label: 'Controlling Person' },
        { value: 'Attorney in Fact', label: 'Attorney in Fact' },
        { value: 'Power of Attorney', label: 'Power of Attorney' },
        { value: 'Chief Executive Officer (CEO)', label: 'Chief Executive Officer (CEO)' },
        { value: 'Chief Financial Officer (CFO)', label: 'Chief Financial Officer (CFO)' },
        { value: 'Chief Operating Officer (COO)', label: 'Chief Operating Officer (COO)' },
        { value: 'Chief Compliance Officer (CCO)', label: 'Chief Compliance Officer (CCO)' },
        { value: 'Director', label: 'Director' },
        { value: 'Chairman', label: 'Chairman' },
        { value: 'Other', label: 'Other' },
      ]
    },
    errorMessage: 'Capacity is required.'
  },
  {
    id: 'CRSE.Signer.3',
    name: 'capacityFieldOther',
    questionText: 'preQualifyingQuestionnaire.CRSE.Signer.3.questionText',
    style: Style.Embedded,
    group: 'Signer Details',
    dynamicUpdate: {
      inputQuestionId: 'CRSE.Signer.2',
      condition: DynamicUpdateCondition.IsNot,
      expectedValue: 'Other',
      action: DynamicUpdateAction.HideAndClear,
      conditionMet: true
    },
    inputs: {
      type: InputType.TextFields,
      fields: [
        { placeholder: 'Capacity', required: true }
      ]
    },
    errorMessage: 'Please provide the capacity in which you are acting.'
  },
];

export default crseQuestions;
